import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  Bookmark,
  CheckCircle2,
  DollarSign,
  FileText,
  GitMerge,
  Layers,
  Send,
  Sparkles,
} from "lucide-react";
import { api, type ActivityEvent, type OverviewResponse, type RecentArticle, type Status } from "../../lib/api.js";
import { StatCard } from "../../components/ui/StatCard.js";
import { Sparkline } from "../../components/ui/Sparkline.js";
import { FunnelCard } from "../../components/ui/FunnelCard.js";
import { SectionCard } from "../../components/ui/SectionCard.js";
import { SkeletonCard, Skeleton } from "../../components/ui/Skeleton.js";
import { EmptyState } from "../../components/ui/EmptyState.js";

const ACTIVITY_ICON: Record<ActivityEvent["kind"], { icon: typeof Sparkles; tint: string }> = {
  enriched: { icon: Layers, tint: "bg-warn-soft text-amber-700" },
  briefed: { icon: Bookmark, tint: "bg-info-soft text-blue-700" },
  produced: { icon: FileText, tint: "bg-brand-50 text-brand-700" },
  refreshed: { icon: CheckCircle2, tint: "bg-accent-soft text-emerald-700" },
};

export function OverviewPage() {
  const { data, isPending, error } = useQuery({
    queryKey: ["dashboard", "overview"],
    queryFn: () => api.getOverview(),
    refetchInterval: 30_000,
  });
  // Month-to-date spend tile — fetched separately so an outage on /api/cost
  // doesn't break the rest of the Overview load.
  const spend = useQuery({
    queryKey: ["dashboard", "spend-mtd"],
    queryFn: () => api.costSummary(),
    refetchInterval: 60_000,
  });

  if (error) {
    return (
      <div className="card p-8 text-sm text-danger">
        Failed to load overview: {(error as Error).message}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-balance text-2xl font-semibold text-ink">Welcome back to AlternaBe Operator</h2>
        <p className="mt-1 text-sm text-muted">
          Your full content + distribution pipeline at a glance. Click any tile for the underlying view.
        </p>
      </div>

      {/* KPI row */}
      <section>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {isPending || !data ? (
            <>
              <SkeletonCard /><SkeletonCard /><SkeletonCard /><SkeletonCard />
            </>
          ) : (
            <>
              <StatCard
                label="Keywords in flight"
                value={data.kpis.totalKeywords}
                hint={`${data.kpis.totalClusters} clusters`}
                icon={Layers}
                tone="brand"
              />
              <StatCard
                label="Briefs ready"
                value={data.kpis.briefsReady}
                hint="LLM-enriched outlines"
                icon={Bookmark}
                tone="accent"
              />
              <StatCard
                label="Articles produced"
                value={data.kpis.articlesProduced}
                hint={`${data.kpis.articlesPublished} published / distributed`}
                icon={FileText}
                tone="brand"
              />
              <StatCard
                label="30-day Claude spend"
                value={`$${data.kpis.spend30dUsd.toFixed(2)}`}
                hint="article production only"
                icon={DollarSign}
                tone="warn"
                sparkline={<Sparkline data={data.kpis.spendSparkline} width={140} height={40} />}
              />
            </>
          )}
        </div>
      </section>

      {/* Funnel + Spend secondary row */}
      {data && (
        <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <FunnelCard stages={data.funnel} total={data.kpis.totalKeywords} />
          </div>
          <ClustersSnapshot clusters={data.clusters} />
        </section>
      )}

      {/* Activity + Recent articles */}
      <section className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        <div className="lg:col-span-3">
          <SectionCard
            title="Recent activity"
            subtitle="Auto-derived from keyword + brief + article timestamps"
            icon={Sparkles}
            padded={false}
          >
            {data ? <ActivityFeed events={data.recentActivity} /> : <ActivitySkeleton />}
          </SectionCard>
        </div>
        <div className="lg:col-span-2">
          <SectionCard
            title="Latest articles"
            icon={FileText}
            padded={false}
            action={
              <a href="#/pipeline" className="text-2xs font-medium text-brand-700 hover:underline">
                View all <ArrowRight className="inline h-3 w-3" />
              </a>
            }
          >
            {data ? <RecentArticles articles={data.recentArticles} /> : <ActivitySkeleton />}
          </SectionCard>
        </div>
      </section>

      {/* Month-to-date spend banner */}
      <section className="flex items-center justify-between gap-4 rounded-lg border border-line bg-white p-4">
        <div>
          <div className="text-2xs font-semibold uppercase tracking-wider text-muted">Spend · month-to-date</div>
          <div className="mt-1 text-2xl font-semibold tabular-nums">
            ${(spend.data?.totalUsd ?? 0).toFixed(2)}
          </div>
          <div className="mt-1 text-xs text-muted">
            {spend.data?.rowCount ?? 0} calls
            {spend.data?.byProvider[0] && ` · top: ${spend.data.byProvider[0].key} ($${spend.data.byProvider[0].totalUsd.toFixed(2)})`}
          </div>
        </div>
        <a href="#/spend" className="btn-ghost text-sm">
          Open Spend dashboard <ArrowRight className="inline h-3 w-3" />
        </a>
      </section>

      {/* Quick-action grid (router hint) */}
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <ActionTile
          icon={GitMerge}
          title="Open Pipeline"
          subtitle="View clusters + keywords"
          href="#/pipeline"
        />
        <ActionTile
          icon={CheckCircle2}
          title="Pending tasks"
          subtitle="Triage the inbox"
          href="#/tasks"
        />
        <ActionTile
          icon={Send}
          title="Send a campaign"
          subtitle="Brevo · Telegram · WhatsApp (soon)"
          href="#/distribution"
        />
      </section>
    </div>
  );
}

function ClustersSnapshot({ clusters }: { clusters: OverviewResponse["clusters"] }) {
  const sorted = [...clusters].sort((a, b) => (b.currentArticleCount - a.currentArticleCount));
  return (
    <SectionCard title="Cluster traction" subtitle="Articles published vs target" icon={Layers} padded={false}>
      <ul className="divide-y divide-line">
        {sorted.slice(0, 6).map((c) => {
          const pct = c.targetArticleCount ? Math.min(100, (c.currentArticleCount / c.targetArticleCount) * 100) : 0;
          return (
            <li key={c.id} className="flex items-center justify-between gap-3 px-5 py-3">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-ink">{c.nameEn}</div>
                <div className="truncate text-2xs text-muted">{c.nameHe}</div>
              </div>
              <div className="w-24 shrink-0">
                <div className="text-right text-2xs font-mono text-muted">
                  {c.currentArticleCount}/{c.targetArticleCount ?? "?"}
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-line-soft">
                  <div className="h-full rounded-full bg-brand-500 transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </SectionCard>
  );
}

function ActivityFeed({ events }: { events: ActivityEvent[] }) {
  if (events.length === 0) {
    return (
      <div className="px-5 py-8">
        <EmptyState
          icon={Sparkles}
          title="No recent activity"
          description="Enrich a keyword from the Pipeline to start populating this feed."
        />
      </div>
    );
  }
  return (
    <ul className="divide-y divide-line">
      {events.map((e, i) => {
        const meta = ACTIVITY_ICON[e.kind];
        const Icon = meta.icon;
        return (
          <li key={`${e.keywordId}-${e.kind}-${i}`} className="flex items-center gap-3 px-5 py-3">
            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${meta.tint}`}>
              <Icon className="h-4 w-4" strokeWidth={2.2} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium text-ink">{kindLabel(e.kind)}</span>
                <span className="truncate text-muted">{e.keyword}</span>
              </div>
              <div className="text-2xs text-muted">{relativeTime(e.ts)} · {e.keywordId}</div>
            </div>
            <a href="#/pipeline" className="text-2xs text-brand-700 hover:underline">Open</a>
          </li>
        );
      })}
    </ul>
  );
}

function RecentArticles({ articles }: { articles: RecentArticle[] }) {
  if (articles.length === 0) {
    return (
      <div className="px-5 py-8">
        <EmptyState
          icon={FileText}
          title="No articles produced yet"
          description="Generate a brief first, then click 'Produce article' on the keyword detail panel."
        />
      </div>
    );
  }
  return (
    <ul className="divide-y divide-line">
      {articles.map((a) => (
        <li key={a.keywordId} className="px-5 py-3">
          <div className="truncate text-sm font-medium text-ink" dir={a.language === "he" ? "rtl" : "ltr"}>
            {a.h1}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-2xs text-muted">
            <span>{a.wordCount}w</span>
            {a.estimatedCostUsd !== null && <span>${a.estimatedCostUsd.toFixed(4)}</span>}
            {a.cluster && <span>{a.cluster}</span>}
            <span>{relativeTime(a.generatedAt)}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}

function ActivitySkeleton() {
  return (
    <ul className="divide-y divide-line">
      {Array.from({ length: 4 }).map((_, i) => (
        <li key={i} className="flex items-center gap-3 px-5 py-3">
          <Skeleton className="h-8 w-8 rounded-lg" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3 w-3/4" />
            <Skeleton className="h-2 w-1/3" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function ActionTile({
  icon: Icon,
  title,
  subtitle,
  href,
}: {
  icon: typeof Sparkles;
  title: string;
  subtitle: string;
  href: string;
}) {
  return (
    <a href={href} className="card card-hover group flex items-center gap-4 p-5">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50 text-brand-700 transition group-hover:bg-brand-100">
        <Icon className="h-5 w-5" strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-ink">{title}</div>
        <div className="truncate text-xs text-muted">{subtitle}</div>
      </div>
      <ArrowRight className="h-4 w-4 text-muted transition group-hover:text-brand-700" />
    </a>
  );
}

function kindLabel(kind: ActivityEvent["kind"]): string {
  switch (kind) {
    case "enriched": return "Enriched";
    case "briefed": return "Brief generated";
    case "produced": return "Article produced";
    case "refreshed": return "Refreshed";
  }
}

function relativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", { month: "short", day: "numeric" });
}

// Silence unused-import warnings — `Status` reserved for future use.
export const _statusType = null as unknown as Status;
